import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { affiliateApi } from '../lib/api';
import { Trophy, Users, DollarSign, ShoppingBag, TrendingUp, Crown, Medal, Award, ArrowRight } from 'lucide-react';

const MEDALS = ['#FFD700', '#C0C0C0', '#CD7F32'];

export default function AffiliateLeaderboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    affiliateApi.getLeaderboard().then(res => { setData(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <main className="pt-20 min-h-screen"><div className="container-ninja section-spacing">
        <div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-20 bg-zinc-900/50 border border-zinc-800" />)}</div>
      </div></main>
    );
  }

  const leaderboard = data?.leaderboard || [];
  const stats = data?.stats || {};

  return (
    <main className="pt-20 min-h-screen" data-testid="affiliate-leaderboard-page">
      <div className="container-ninja section-spacing max-w-4xl mx-auto">
        <Link to="/affiliates" className="text-zinc-500 text-xs uppercase tracking-wider hover:text-zinc-300 transition-colors mb-3 inline-block" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Affiliate Program</Link>
        <h1 className="text-white text-4xl sm:text-5xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="affiliate-lb-title">Top Affiliates</h1>
        <p className="text-zinc-400 text-sm mb-8 max-w-md">Our highest-performing partners driving the NinjAthlete community.</p>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-10">
          <div className="bg-zinc-900/60 border border-zinc-800 p-5 text-center">
            <Users className="w-5 h-5 text-zinc-600 mx-auto mb-2" />
            <span className="text-white text-2xl font-bold block" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{stats.total_affiliates || 0}</span>
            <span className="text-zinc-500 text-[10px] uppercase tracking-wider">Partners</span>
          </div>
          <div className="bg-zinc-900/60 border border-zinc-800 p-5 text-center">
            <ShoppingBag className="w-5 h-5 text-zinc-600 mx-auto mb-2" />
            <span className="text-white text-2xl font-bold block" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{stats.total_orders || 0}</span>
            <span className="text-zinc-500 text-[10px] uppercase tracking-wider">Total Orders</span>
          </div>
          <div className="bg-zinc-900/60 border border-zinc-800 p-5 text-center">
            <DollarSign className="w-5 h-5 text-zinc-600 mx-auto mb-2" />
            <span className="text-white text-2xl font-bold block" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>${Math.round(stats.total_revenue || 0).toLocaleString()}</span>
            <span className="text-zinc-500 text-[10px] uppercase tracking-wider">Revenue Driven</span>
          </div>
        </div>

        {/* Podium for top 3 */}
        {leaderboard.length >= 3 && (
          <div className="flex items-end justify-center gap-4 mb-10 pt-4" data-testid="affiliate-podium">
            {[1, 0, 2].map(idx => {
              const a = leaderboard[idx];
              if (!a) return null;
              const isFirst = idx === 0;
              return (
                <div key={a.code} className={`text-center ${isFirst ? 'w-36' : 'w-28'}`}>
                  <div className={`${isFirst ? 'w-16 h-16' : 'w-12 h-12'} mx-auto bg-zinc-800 border-2 flex items-center justify-center text-zinc-400 font-bold uppercase mb-2`}
                    style={{ borderColor: MEDALS[idx], fontFamily: 'Bebas Neue, sans-serif', fontSize: isFirst ? '1.2rem' : '1rem' }}>
                    {a.name?.charAt(0) || a.code?.charAt(0) || '?'}
                  </div>
                  <div className={`bg-zinc-800/60 ${isFirst ? 'py-5' : 'py-3'} border-t-2`} style={{ borderColor: MEDALS[idx] }}>
                    {isFirst && <Trophy className="w-5 h-5 mx-auto mb-1" style={{ color: MEDALS[0] }} />}
                    <span className={`${isFirst ? 'text-xl' : 'text-lg'} font-bold block`} style={{ fontFamily: 'Bebas Neue, sans-serif', color: MEDALS[idx] }}>{idx + 1}</span>
                    <span className="text-white text-xs block truncate px-1">{a.name || a.code}</span>
                    <span className="text-zinc-500 text-[10px]">{a.total_orders || 0} orders</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Full table */}
        <div className="space-y-1" data-testid="affiliate-leaderboard-table">
          {leaderboard.length > 0 ? leaderboard.map((a, i) => (
            <div key={a.code} className="flex items-center gap-4 p-4 bg-zinc-900/40 border border-zinc-800 hover:border-zinc-700 transition-colors" data-testid={`affiliate-row-${i}`}>
              <span className={`w-8 text-center text-sm font-bold ${i < 3 ? '' : 'text-zinc-600'}`} style={{ fontFamily: 'Bebas Neue, sans-serif', color: i < 3 ? MEDALS[i] : undefined }}>
                {a.rank}
              </span>
              <div className="w-10 h-10 bg-zinc-800 border border-zinc-700 flex items-center justify-center text-zinc-400 font-bold uppercase" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                {a.name?.charAt(0) || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-white text-sm block truncate">{a.name || a.code}</span>
                <span className="text-zinc-600 text-[10px] uppercase tracking-wider">Code: {a.code}</span>
              </div>
              <div className="text-right">
                <span className="text-white text-sm font-bold block">{a.total_orders || 0}</span>
                <span className="text-zinc-600 text-[10px]">orders</span>
              </div>
              <div className="text-right">
                <span className="text-[#00F0FF] text-sm font-bold block">${Math.round(a.total_revenue || 0).toLocaleString()}</span>
                <span className="text-zinc-600 text-[10px]">revenue</span>
              </div>
            </div>
          )) : (
            <div className="text-center py-16 bg-zinc-900/40 border border-zinc-800">
              <Trophy className="w-10 h-10 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-400 text-sm mb-4">No affiliates on the leaderboard yet.</p>
              <Link to="/affiliates" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#E63946] text-white text-xs uppercase tracking-wider hover:bg-[#E63946]/80 transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Join the Affiliate Program <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="mt-10 p-6 bg-gradient-to-r from-[#E63946]/10 to-transparent border border-[#E63946]/30 text-center" data-testid="affiliate-cta">
          <h2 className="text-white text-xl mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Want to Join?</h2>
          <p className="text-zinc-400 text-sm mb-4">Earn commissions on every sale. Share your code and climb the leaderboard.</p>
          <Link to="/affiliates" className="inline-flex items-center gap-2 px-6 py-3 bg-[#E63946] text-white text-xs uppercase tracking-wider hover:bg-[#E63946]/80 transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Apply Now <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>
    </main>
  );
}
